import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import { LockOutlined, HourglassEmptyOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { IAIPlanLimit } from '@/common/interface';
import { AppEvent, emitter } from '@/plugin/mitt';
import { useAppDispatch } from '@/store/hooks';
import { fetchAIUsageRequest } from '@/store/slices/aiUsageSlice';

const FEATURE_KEYS = ['speaking', 'writing', 'conversation', 'reading', 'vocabulary', 'assessment'];

/** Global modal shown when the backend blocks an AI request because of the user's plan. */
const AIPlanLimitModal: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [limit, setLimit] = useState<IAIPlanLimit | null>(null);

    useEffect(() => {
        const handler = (data: IAIPlanLimit) => {
            setLimit(data);
            // A blocked request from the server means the cached plan may be stale.
            if (data.message) {
                dispatch(fetchAIUsageRequest());
            }
        };
        emitter.on(AppEvent.AI_PLAN_LIMIT, handler);
        return () => {
            emitter.off(AppEvent.AI_PLAN_LIMIT, handler);
        };
    }, [dispatch]);

    if (!limit) {
        return null;
    }

    const isQuotaExceeded = limit.code === 'AI_QUOTA_EXCEEDED';
    const featureKey = FEATURE_KEYS.includes(limit.feature) ? limit.feature : 'default';
    const feature = t(`common.aiPlanLimit.feature.${featureKey}`);
    const Icon = isQuotaExceeded ? HourglassEmptyOutlined : LockOutlined;

    return (
        <Dialog open onClose={() => setLimit(null)} maxWidth="xs" fullWidth>
            <DialogContent sx={{ textAlign: 'center', pt: 4 }}>
                <Box
                    sx={{
                        width: 64,
                        height: 64,
                        mx: 'auto',
                        mb: 2,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: isQuotaExceeded ? 'warning.light' : 'info.light',
                        color: isQuotaExceeded ? 'warning.dark' : 'info.dark',
                    }}
                >
                    <Icon fontSize="large" />
                </Box>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                    {t(
                        isQuotaExceeded
                            ? 'common.aiPlanLimit.title.quotaExceeded'
                            : 'common.aiPlanLimit.title.notInPlan',
                    )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {isQuotaExceeded
                        ? t('common.aiPlanLimit.description.quotaExceeded', {
                              feature,
                              used: limit.used,
                              limit: limit.limit,
                              period: t(
                                  `common.aiPlanLimit.period.${limit.period === 'monthly' ? 'monthly' : 'daily'}`,
                              ),
                          })
                        : t('common.aiPlanLimit.description.notInPlan', { feature })}
                </Typography>
                {isQuotaExceeded && limit.resets_at && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t('common.aiPlanLimit.description.resetsAt', {
                            resetsAt: dayjs(limit.resets_at).format('HH:mm DD/MM/YYYY'),
                        })}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button variant="contained" fullWidth onClick={() => setLimit(null)}>
                    {t('common.aiPlanLimit.gotIt')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AIPlanLimitModal;
